import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const getAgeLabel = (value) => {
  const map = {
    A00: "All Ages (14-99)",
    A01: "14-18",
    A02: "19-21",
    A03: "22-24",
    A04: "25-34",
    A05: "35-44",
    A06: "45-54",
    A07: "55-64",
    A08: "65-99",
  };
  return map[value] || "Unknown";
};

const AgeGroupEmployeeChart = ({ data }) => {
  const pdfRef = useRef();
  const safeData = data ?? [];

  if (!safeData.length) {
    return <p>No data available</p>;
  }

  const years = Object.keys(safeData[0]).filter((key) =>
    key.startsWith("year_"),
  );

  const latestYear = years[years.length - 1];

  // =========================
  // Summary Calculations
  // =========================
  const totalEmployees = safeData.reduce((sum, row) => {
    return (
      sum +
      years.reduce((rowSum, year) => rowSum + (parseInt(row[year]) || 0), 0)
    );
  }, 0);

  const highestAverage = safeData.reduce(
    (max, row) =>
      parseInt(row.average || 0) > parseInt(max.average || 0) ? row : max,
    safeData[0],
  );

  // =========================
  // Chart Data
  // =========================
  const colorPalette = [
    "#014CB2",
    "#EC4899",
    "#22c55e",
    "#f59e0b",
    "#8b5cf6",
    "#ef4444",
    "#10b981",
    "#6366f1",
  ];

  const chartData = {
    labels: years.map((year) => year.replace("year_", "")),
    datasets: safeData.map((item, index) => ({
      label: getAgeLabel(item.agegrp_label),
      data: years.map((year) => parseInt(item[year] || 0)),
      backgroundColor: colorPalette[index % colorPalette.length],
      borderRadius: 6,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index" },
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Employee Distribution by Age Group (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        title: { display: true, text: "Year" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#f1f5f9" },
        title: { display: true, text: "Employees" },
        ticks: {
          callback: (value) => value.toLocaleString(),
        },
      },
    },
  };

  // =========================
  // PDF Export
  // =========================
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text("20250916.v3.198", 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("age_group_employee_analysis.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-8 bg-white rounded-2xl shadow-xl">
        <h2 className="text-2xl font-bold mb-8 text-center text-gray-800">
          Employee Data by Age Group
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 text-center">
          <div className="p-5 bg-blue-50 rounded-xl shadow">
            <p className="text-sm font-medium text-gray-500">Latest Year</p>
            <p className="text-2xl font-bold text-blue-700">
              {latestYear.replace("year_", "")}
            </p>
          </div>

          <div className="p-5 bg-green-50 rounded-xl shadow">
            <p className="text-sm font-medium text-gray-500">
              Total Employees (All Years)
            </p>
            <p className="text-2xl font-bold text-green-700">
              {totalEmployees.toLocaleString()}
            </p>
          </div>

          <div className="p-5 bg-yellow-50 rounded-xl shadow">
            <p className="text-sm font-medium text-gray-500">Highest Average</p>
            <p className="text-2xl font-bold text-yellow-600">
              {getAgeLabel(highestAverage.agegrp_label)}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-3">Age Group</th>
                  {years.map((year) => (
                    <th key={year} className="p-3">
                      {year.replace("year_", "")}
                    </th>
                  ))}
                  <th className="p-3">Average</th>
                </tr>
              </thead>
              <tbody>
                {safeData.map((row) => (
                  <tr
                    key={row.agegrp_label}
                    className={`hover:bg-gray-50 ${
                      row.agegrp_label === highestAverage.agegrp_label
                        ? "bg-yellow-50 font-semibold"
                        : ""
                    }`}
                  >
                    <td className="p-3">{getAgeLabel(row.agegrp_label)}</td>
                    {years.map((year) => (
                      <td key={year} className="p-3">
                        {Number(row[year] || 0).toLocaleString()}
                      </td>
                    ))}
                    <td className="p-3">
                      {Number(row.average || 0).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-xl shadow p-6 h-96">
            <Bar data={chartData} options={options} />
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-6">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-6 py-3 text-white rounded-xl flex items-center gap-3 shadow-lg transition-all hover:scale-105"
        >
          <span className="font-medium">Download as PDF</span>
          <img className="w-6" src={pdfIcon} alt="pdf" />
        </button>
      </div>
    </>
  );
};

export default AgeGroupEmployeeChart;
